import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FieldLabelComponent } from '../field-label/field-label.component';

export interface SelectOption<T = string | number> {
  value: T;
  label: string;
  disabled?: boolean;
}

let idCounter = 0;
const nextId = (): string => `bx-field-select-${++idCounter}`;

/**
 * R-FORM-02/03/04/05 — single labeled select with custom chevron and the same focus/error
 * affordances as FieldInput. `appearance: none` + an inline SVG chevron — never the
 * browser-default arrow.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-field-select',
  standalone: true,
  imports: [FieldLabelComponent],
  templateUrl: './field-select.component.html',
  styleUrls: ['./field-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldSelectComponent),
      multi: true,
    },
  ],
})
export class FieldSelectComponent<T extends string | number = string> implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly options = input.required<SelectOption<T>[]>();
  readonly placeholder = input<string>('Select…');
  readonly required = input<boolean>(false);
  readonly hint = input<string | undefined>(undefined);
  readonly error = input<string | undefined>(undefined);
  readonly inputId = input<string>(nextId());

  protected readonly value = signal<T | null>(null);
  protected readonly disabled = signal(false);

  protected readonly errorId = computed(() => `${this.inputId()}-error`);
  protected readonly describedBy = computed(() => (this.error() ? this.errorId() : null));
  protected readonly invalid = computed(() => !!this.error());

  private onChange: (value: T | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: T | null): void {
    this.value.set(value);
  }
  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected handleChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const raw = target.value;
    if (raw === '') {
      this.value.set(null);
      this.onChange(null);
      return;
    }
    // Find the matching option to preserve its native type (number vs string).
    const match = this.options().find(o => String(o.value) === raw);
    const next = (match ? match.value : (raw as unknown as T)) as T;
    this.value.set(next);
    this.onChange(next);
  }

  protected handleBlur(): void {
    this.onTouched();
  }
}
